import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/ui/components/ui/card";
import { Button } from "@/ui/components/ui/button";
import { Progress } from "@/ui/components/ui/progress";
import {
  CheckCircle2,
  Circle,
  Lock,
  Rocket,
  X,
  ChevronRight,
  PartyPopper,
} from "lucide-react";
import { useFirstSteps } from "@/controllers/hooks/useFirstSteps";

export function FirstStepsChecklist() {
  const {
    steps,
    completedCount,
    totalCount,
    allCompleted,
    dismissed,
    dismiss,
    isLoading,
  } = useFirstSteps();
  const navigate = useNavigate();

  if (isLoading || dismissed || steps.length === 0) return null;

  const progressPercentage = Math.round((completedCount / totalCount) * 100);

  if (allCompleted) {
    return (
      <Card className="relative overflow-hidden border border-green-200 bg-gradient-to-br from-white to-green-50/30 shadow-figma-md rounded-xl">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-green-400 to-emerald-500" />
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                <PartyPopper className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">
                  Primeiros Passos Completos!
                </h3>
                <p className="text-xs text-green-600">
                  Voce concluiu todos os passos iniciais
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={dismiss}
              className="text-gray-400 hover:text-gray-600 h-8 w-8"
              aria-label="Fechar"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="relative overflow-hidden border border-accent-600/20 bg-white shadow-figma-md rounded-xl">
      {/* Decorative accent line */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-accent-600 to-amber-400" />

      <CardContent className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-accent-600/10 flex items-center justify-center">
              <Rocket className="w-5 h-5 text-accent-600" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">
                Primeiros Passos
              </h3>
              <p className="text-xs text-gray-500">
                {completedCount}/{totalCount} completados
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={dismiss}
            className="text-gray-400 hover:text-gray-600 h-8 w-8"
            aria-label="Fechar checklist"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Progress bar */}
        <div className="mb-5">
          <div className="flex justify-between items-center mb-1.5">
            <span className="text-xs text-gray-500">Progresso</span>
            <span className="text-xs font-semibold text-accent-600">
              {progressPercentage}%
            </span>
          </div>
          <Progress value={progressPercentage} className="h-2" />
        </div>

        {/* Steps */}
        <ul className="space-y-2">
          {steps.map((step, index) => {
            const isLocked =
              !step.completed && index > 0 && !steps[index - 1].completed;

            return (
              <li key={step.key}>
                <button
                  onClick={() => {
                    if (!isLocked && step.href && !step.completed) {
                      navigate(step.href);
                    }
                  }}
                  disabled={isLocked || step.completed}
                  className={`flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm transition-colors ${
                    step.completed
                      ? "text-green-600 bg-green-50/50"
                      : isLocked
                        ? "text-gray-400 cursor-default"
                        : "text-gray-700 hover:bg-gray-50 cursor-pointer"
                  }`}
                >
                  {step.completed ? (
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                  ) : isLocked ? (
                    <Lock className="w-5 h-5 text-gray-300 flex-shrink-0" />
                  ) : (
                    <Circle className="w-5 h-5 text-accent-600 flex-shrink-0" />
                  )}
                  <span className="flex-1 text-left">{step.label}</span>
                  {!step.completed && !isLocked && step.href && (
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}
